"use client";

import { c as _c } from "react/compiler-runtime";
import { RefObject, useEffect, useRef, useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
type Options = {
  rootRef: RefObject<HTMLElement | null>;
  scrollRef?: RefObject<HTMLElement | null>;
};

// Below this distance-from-bottom, snap to the bottom so the latest message
// pins above the input.
const BOTTOM_SNAP_PX = 40;

/**
 * Keeps a full-screen input modal usable when the mobile keyboard opens: pins
 * the root to `visualViewport` (header fixed, bottom input above the keyboard)
 * and, via `scrollRef`, preserves the list's distance-from-bottom so the message
 * you're on stays anchored. iOS Safari needs this — there's no CSS equivalent.
 * Wire `onInputFocus`/`onInputBlur` to the input; read `keyboardOpen` for styling.
 */
export function useKeyboardViewport(t0) {
  const $ = _c(18);
  const {
    rootRef,
    scrollRef
  } = t0;
  const isMobile = useIsMobile();
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const anchorRef = useRef(0);
  let t1;
  if ($[0] !== scrollRef) {
    t1 = () => {
      const s = scrollRef?.current;
      const d = s ? s.scrollHeight - s.scrollTop - s.clientHeight : 0;
      anchorRef.current = d < BOTTOM_SNAP_PX ? 0 : d;
    };
    $[0] = scrollRef;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const captureAnchor = t1;
  let t2;
  if ($[2] !== captureAnchor || $[3] !== isMobile) {
    t2 = () => {
      if (!isMobile) {
        return;
      }
      captureAnchor();
      setKeyboardOpen(true);
    };
    $[2] = captureAnchor;
    $[3] = isMobile;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const onInputFocus = t2;
  let t3;
  if ($[5] !== captureAnchor || $[6] !== isMobile) {
    t3 = () => {
      if (!isMobile) {
        return;
      }
      captureAnchor();
      setKeyboardOpen(false);
    };
    $[5] = captureAnchor;
    $[6] = isMobile;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const onInputBlur = t3;
  let t4;
  let t5;
  if ($[8] !== isMobile || $[9] !== keyboardOpen || $[10] !== rootRef || $[11] !== scrollRef) {
    t4 = () => {
      if (!isMobile) {
        return;
      }
      const clear = () => {
        const el = rootRef.current;
        if (!el) {
          return;
        }
        for (const p of ["position", "left", "right", "top", "height", "transform", "paddingTop", "paddingLeft", "paddingRight"] as const) {
          el.style[p] = "";
        }
      };
      const anchorScroll = () => {
        const s_0 = scrollRef?.current;
        if (s_0) {
          s_0.scrollTop = s_0.scrollHeight - s_0.clientHeight - anchorRef.current;
        }
      };
      if (!keyboardOpen) {
        clear();
        anchorScroll();
        return;
      }
      const vv = window.visualViewport;
      if (!vv) {
        return;
      }
      const update = () => {
        const el_0 = rootRef.current;
        if (!el_0) {
          return;
        }
        el_0.style.position = "fixed";
        el_0.style.left = "0";
        el_0.style.right = "0";
        el_0.style.top = "0";
        el_0.style.height = `${vv.height}px`;
        el_0.style.transform = `translateY(${vv.offsetTop}px)`;
        el_0.style.paddingTop = "8px";
        el_0.style.paddingLeft = "8px";
        el_0.style.paddingRight = "8px";
        anchorScroll();
      };
      update();
      vv.addEventListener("resize", update);
      vv.addEventListener("scroll", update);
      return () => {
        vv.removeEventListener("resize", update);
        vv.removeEventListener("scroll", update);
      };
    };
    t5 = [isMobile, keyboardOpen, rootRef, scrollRef];
    $[8] = isMobile;
    $[9] = keyboardOpen;
    $[10] = rootRef;
    $[11] = scrollRef;
    $[12] = t4;
    $[13] = t5;
  } else {
    t4 = $[12];
    t5 = $[13];
  }
  useEffect(t4, t5);
  let t6;
  if ($[14] !== keyboardOpen || $[15] !== onInputBlur || $[16] !== onInputFocus) {
    t6 = {
      keyboardOpen,
      onInputFocus,
      onInputBlur
    };
    $[14] = keyboardOpen;
    $[15] = onInputBlur;
    $[16] = onInputFocus;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  return t6;
}